import React, { useState } from 'react'

const BATTERY_RECOVERED_OPTIONS = [
  { value: 'yes',            label: 'Yes'            },
  { value: 'no',             label: 'No'             },
  { value: 'amount_dispute', label: 'Amount Dispute'  },
]

const NO_REASON_OPTIONS = [
  'Customer Not Traceable',
  'VIN id is missing',
  'Customer mobile number is missing',
]

const TICKET_STATUS_OPTIONS = [
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed',  label: 'Closed'  },
]

function parseBatteryRecovered(raw) {
  if (!raw) return { primary: '', noReason: '' }
  if (raw.startsWith('no-')) return { primary: 'no', noReason: raw.slice(3) }
  return { primary: raw, noReason: '' }
}

const emptyForm = (resolvedData) => {
  const rf = resolvedData?.resolution_fields || {}
  const { primary, noReason } = parseBatteryRecovered(rf.battery_recovered)
  return {
    battery_recovered: primary,
    no_reason:         noReason,
    remarks:           rf.remarks || '',
    ticket_status:     resolvedData?.ticket_status  || '',
    issue_resolved:    resolvedData?.issue_resolved || '',
  }
}

export default function AarCloseForm({ resolvedData, readOnly, onSubmit, submitting }) {
  const [form, setForm] = useState(() => emptyForm(resolvedData))
  const [errors, setErrors] = useState({})

  const isClosed  = form.ticket_status === 'closed'
  const isNoSelected = form.battery_recovered === 'no'

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'battery_recovered' && value !== 'no') next.no_reason = ''
      if (field === 'ticket_status' && value !== 'closed') next.issue_resolved = ''
      return next
    })
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  function validate() {
    const e = {}
    if (!form.battery_recovered)               e.battery_recovered = 'Required'
    if (isNoSelected && !form.no_reason)        e.no_reason         = 'Required'
    if (!form.ticket_status)                    e.ticket_status     = 'Required'
    if (isClosed && !form.issue_resolved)       e.issue_resolved    = 'Required'
    return e
  }

  function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    const batteryValue = isNoSelected
      ? `no-${form.no_reason}`
      : form.battery_recovered

    onSubmit({
      resolution_fields: {
        battery_recovered: batteryValue,
        remarks:           form.remarks,
      },
      ticket_status: form.ticket_status,
      ...(isClosed && { issue_resolved: form.issue_resolved }),
    })
  }

  return (
    <form className="ctf-section" onSubmit={handleSubmit} noValidate>

      {readOnly && (
        <div className="ctf-readonly-banner">
          &#128274; This ticket is closed — view only
        </div>
      )}

      {/* Battery Recovered */}
      <Field label="Battery Recovered" required={!readOnly} error={errors.battery_recovered}>
        <select
          className="ctf-select"
          value={form.battery_recovered}
          onChange={(e) => set('battery_recovered', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select option</option>
          {BATTERY_RECOVERED_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* 2nd value — only when No is selected */}
      {isNoSelected && (
        <Field label="Reason" required={!readOnly} error={errors.no_reason}>
          <select
            className="ctf-select"
            value={form.no_reason}
            onChange={(e) => set('no_reason', e.target.value)}
            disabled={readOnly}
          >
            <option value="">Select reason</option>
            {NO_REASON_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </Field>
      )}

      {/* Remarks */}
      <Field label="Remarks" error={errors.remarks}>
        <textarea
          className="ctf-textarea"
          value={form.remarks}
          onChange={(e) => set('remarks', e.target.value)}
          placeholder="Add any remarks…"
          readOnly={readOnly}
        />
      </Field>

      {/* ── Divider before status fields ── */}
      <div className="ctf-divider" />

      {/* Ticket Status */}
      <Field label="Ticket Status" required={!readOnly} error={errors.ticket_status}>
        <select
          className="ctf-select"
          value={form.ticket_status}
          onChange={(e) => set('ticket_status', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select status</option>
          {TICKET_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* Issue Resolved — only active when ticket_status is closed */}
      <Field
        label="Issue Resolved"
        required={isClosed && !readOnly}
        error={errors.issue_resolved}
        hint={!isClosed && !readOnly ? 'Available only when Ticket Status is Closed' : undefined}
      >
        <div className={`ctf-radio-group ${(!isClosed || readOnly) ? 'ctf-radio-group--disabled' : ''}`}>
          {['yes', 'no'].map((v) => (
            <label key={v} className="ctf-radio-label">
              <input
                type="radio"
                name="issue_resolved"
                value={v}
                checked={form.issue_resolved === v}
                disabled={!isClosed || readOnly}
                onChange={() => set('issue_resolved', v)}
                className="ctf-radio"
              />
              {v === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      </Field>

      {!readOnly && (
        <button type="submit" className="ctf-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Update Ticket'}
        </button>
      )}
    </form>
  )
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="ctf-field">
      <span className="ctf-label">
        {label}
        {required && <span className="ctf-required">*</span>}
      </span>
      {children}
      {hint  && !error && <span className="ctf-hint">{hint}</span>}
      {error && <span className="ctf-field-error">{error}</span>}
    </div>
  )
}
